/**
 * The closed recipe-tag vocabulary (spec: docs/superpowers/specs/2026-09-05-recipe-tag-taxonomy-design.md §4,
 * §4.1 addendum for the `protein` facet).
 *
 * - `authored`   values are chosen by a human (seed author, recipe author, reviewed proposal).
 * - `derived`    values are recomputed from structured fields on every write and never hand-authored
 *                (quick ⇔ total time ≤ 30, budget ⇔ costEstimate budget, no-cook ⇔ cookTime 0).
 * - `diet`       values are proven/disproven from ingredients (deriveDietaryTags), from per-serving net
 *                carbs (deriveNutritionDiets: keto/low_carb, #829), or from a human allowlist (paleo
 *                only, as of #829); they are accepted on input because a deriver or allowlist validates
 *                them.
 * - `ingredient` values (§4.1) are proven/disproven from resolved ingredients (deriveProteinTags,
 *                packages/database/src/curated-data/derive-protein-tags.ts), the same proven/disproven
 *                contract as `diet` — accepted on input because the deriver validates them.
 *
 * Diet values keep underscores on purpose: they are the existing DIETARY_RESTRICTION_TAGS enum used by
 * stored user preferences and the eligibility filter. Everything else is kebab-case. Users never see the
 * stored form — render with tagLabel().
 */
export const TAG_FACETS = ["protein", "diet", "time", "cost", "method", "household", "taste"] as const;
export type TagFacet = (typeof TAG_FACETS)[number];
export type TagKind = "authored" | "derived" | "diet" | "ingredient";
export type TagStatus = "active" | "incubating";

export interface TagDefinition {
  value: string;
  facet: TagFacet;
  label: string;
  kind: TagKind;
  /** `incubating` = allowed but exempt from the ≥3-recipes corpus rule until it earns them. */
  status: TagStatus;
  /** Accepted on input, never stored. Lower-case; separators are also matched loosely (see canonicalizeTag). */
  aliases: readonly string[];
}

const def = (
  value: string,
  facet: TagFacet,
  label: string,
  kind: TagKind,
  aliases: readonly string[] = [],
  status: TagStatus = "active",
): TagDefinition => ({ value, facet, label, kind, status, aliases });

export const RECIPE_TAGS: readonly TagDefinition[] = [
  // protein (§4.1 addendum, 2026-09-08) — proven/disproven by deriveProteinTags
  // (packages/database/src/curated-data/derive-protein-tags.ts) from resolved
  // ingredients. Listed first in TAG_FACETS so protein pills lead the tag row.
  def("beef", "protein", "Beef", "ingredient"),
  def("chicken", "protein", "Chicken", "ingredient"),
  def("turkey", "protein", "Turkey", "ingredient"),
  def("pork", "protein", "Pork", "ingredient"),
  // Corpus rule (§4, §4.1): active only with >=3 curated recipes; lamb is
  // expected to stay incubating until the catalogue grows lamb recipes.
  def("lamb", "protein", "Lamb", "ingredient", [], "incubating"),
  def("seafood", "protein", "Seafood", "ingredient", ["fish", "shellfish"]),
  def("eggs", "protein", "Eggs", "ingredient", ["egg"]),
  def("plant-protein", "protein", "Plant protein", "ingredient", ["plant protein", "plant-based"]),
  // diet — order here is display order within the facet
  def("vegetarian", "diet", "Vegetarian", "diet"),
  def("vegan", "diet", "Vegan", "diet"),
  def("pescatarian", "diet", "Pescatarian", "diet", ["pescetarian"]),
  def("gluten_free", "diet", "Gluten-free", "diet", ["gluten-free", "gluten free", "glutenfree"]),
  def("dairy_free", "diet", "Dairy-free", "diet", ["dairy-free", "dairy free", "dairyfree"]),
  // keto (#829): no longer a human allowlist -- proven/disproven from per-serving net
  // carbs (deriveNutritionDiets, packages/database/src/curated-data/derive-nutrition-diets.ts),
  // same proven/disproven contract as the ingredient-derived diet values above.
  def("keto", "diet", "Keto", "diet"),
  // low_carb (#829): same nutrition-derived deriver as keto; every keto recipe is also
  // low_carb (16g <= 33g threshold). "low-carb" stays retired below -- the old authored
  // tag is stripped and re-derived, never trusted.
  def("low_carb", "diet", "Low-carb", "diet"),
  def("paleo", "diet", "Paleo", "diet"),
  // time / cost — recomputed
  def("quick", "time", "Quick (≤30 min)", "derived"),
  def("budget", "cost", "Budget", "derived"),
  // method
  def("one-pot", "method", "One-pot", "authored", ["one pot", "onepot"]),
  def("one-pan", "method", "One-pan", "authored", ["one pan", "onepan"]),
  def("sheet-pan", "method", "Sheet-pan", "authored", ["sheet pan", "sheetpan"]),
  def("no-cook", "method", "No-cook", "derived", ["no_cook", "no cook", "nocook"]),
  // household fit
  def("family-friendly", "household", "Kid-friendly", "authored", ["kid-friendly", "kid friendly", "family friendly", "kidfriendly"]),
  def("meal-prep", "household", "Meal-prep", "authored", ["meal prep", "mealprep"]),
  def("freezer-friendly", "household", "Freezer-friendly", "authored", ["freezer friendly"], "incubating"),
  // taste
  def("comfort-food", "taste", "Comfort food", "authored", ["comfort food", "comfortfood"]),
  def("spicy", "taste", "Spicy", "authored"),
];

/** Values that once existed and must be stripped on every write. Never re-creatable. */
export const RETIRED_RECIPE_TAGS: readonly string[] = [
  "healthy",
  "couple-plan",
  "day-1", "day-2", "day-3", "day-4", "day-5", "day-6", "day-7",
  "hellofresh",
  "source-import",
  "high-protein",
  "low-carb",
  "nut-free",
];

export const RECOMPUTED_TAG_VALUES = ["quick", "budget", "no-cook"] as const;

export const ACTIVE_TAG_VALUES: readonly string[] = RECIPE_TAGS.map((t) => t.value);
export const AUTHORED_TAG_VALUES: readonly string[] = RECIPE_TAGS.filter((t) => t.kind === "authored").map((t) => t.value);
export const DIET_TAG_VALUES: readonly string[] = RECIPE_TAGS.filter((t) => t.facet === "diet").map((t) => t.value);
/** The 8 protein (ingredient-kind) values, in registry/display order (§4.1 addendum). */
export const PROTEIN_TAG_VALUES: readonly string[] = RECIPE_TAGS.filter((t) => t.facet === "protein").map((t) => t.value);

const BY_VALUE: ReadonlyMap<string, TagDefinition> = new Map(RECIPE_TAGS.map((t) => [t.value, t]));

/** Every value and alias, lower-cased, → canonical value. */
const ALIAS_TO_VALUE: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const t of RECIPE_TAGS) {
    m.set(t.value, t.value);
    for (const a of t.aliases) m.set(a.toLowerCase(), t.value);
  }
  return m;
})();

const RETIRED_SET = new Set(RETIRED_RECIPE_TAGS);

function keyVariants(input: string): string[] {
  const key = input.trim().toLowerCase();
  if (key === "") return [];
  // Match separators loosely: "Gluten Free", "gluten_free", "gluten-free" all reach the same entry.
  return [key, key.replace(/[\s_-]+/g, "-"), key.replace(/[\s_-]+/g, "_"), key.replace(/[\s_-]+/g, " ")];
}

/** Canonical stored value for an input spelling, or null when unknown/retired. */
export function canonicalizeTag(input: string): string | null {
  const variants = keyVariants(input);
  // Retirement wins over any separator-variant match against an active value's
  // OWN spelling (e.g. "low-carb" retired vs. active "low_carb", #829) -- a
  // retired value must never be silently resurrected just because its hyphenated
  // form happens to normalize to a live value's underscore form. Checked against
  // the RAW (trim + lowercase only, no separator substitution) spelling only --
  // variants[0] -- so the exact retired string is blocked without also catching
  // the active value's own separator variants (which share the same normalized
  // form and would otherwise collide with this check).
  if (variants.length > 0 && RETIRED_SET.has(variants[0]!)) return null;
  for (const k of variants) {
    const v = ALIAS_TO_VALUE.get(k);
    if (v) return v;
  }
  return null;
}

export function isRetiredTag(input: string): boolean {
  return keyVariants(input).some((k) => RETIRED_SET.has(k));
}

export function tagLabel(value: string): string {
  return BY_VALUE.get(value)?.label ?? value;
}

export function tagFacet(value: string): TagFacet | undefined {
  return BY_VALUE.get(value)?.facet;
}

const ORDER_INDEX: ReadonlyMap<string, number> = new Map(RECIPE_TAGS.map((t, i) => [t.value, i]));
const FACET_INDEX: ReadonlyMap<TagFacet, number> = new Map(TAG_FACETS.map((f, i) => [f, i]));

/** Stable sort: facet order (TAG_FACETS), then registry order within the facet; unknown values last, alphabetically. */
export function compareTagOrder(a: string, b: string): number {
  const da = BY_VALUE.get(a);
  const db = BY_VALUE.get(b);
  if (!da && !db) return a.localeCompare(b);
  if (!da) return 1;
  if (!db) return -1;
  const fa = FACET_INDEX.get(da.facet)!;
  const fb = FACET_INDEX.get(db.facet)!;
  if (fa !== fb) return fa - fb;
  return ORDER_INDEX.get(a)! - ORDER_INDEX.get(b)!;
}
